import { createContext, useContext, useState, ReactNode, FC } from "react";
import ReactDOM from "react-dom";

interface TooltipState {
  visible: boolean;
  content: ReactNode | null;
  x: number;
  y: number;
}

interface TooltipContextType {
  showTooltip: (content: ReactNode, x: number, y: number) => void;
  hideTooltip: () => void;
}

const TooltipContext = createContext<TooltipContextType | undefined>(undefined);

export const TooltipProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    content: null,
    x: 0,
    y: 0,
  });

  const showTooltip = (content: ReactNode, x: number, y: number) => {
    setTooltip({ visible: true, content, x, y });
  };

  const hideTooltip = () => {
    setTooltip((t) => ({ ...t, visible: false }));
  };

  const tooltipRoot = document.getElementById("tooltip-root");
  if (!tooltipRoot) {
    console.warn(
      "Tooltip root not found. Please add <div id='tooltip-root'></div> in index.html",
    );
  }

  return (
    <TooltipContext.Provider value={{ showTooltip, hideTooltip }}>
      {children}
      {tooltipRoot &&
        ReactDOM.createPortal(
          tooltip.visible && (
            <div
              className="fixed z-9999 pointer-events-none transition-opacity duration-200"
              style={{
                top: tooltip.y,
                left: tooltip.x,
                transform: "translate(-50%, -100%)",
                opacity: tooltip.visible ? 1 : 0,
              }}
            >
              {tooltip.content}
            </div>
          ),
          tooltipRoot,
        )}
    </TooltipContext.Provider>
  );
};

export const useTooltip = (): TooltipContextType => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error("useTooltip must be used within a TooltipProvider");
  }
  return context;
};
